<<template>
    <div class="hub-page">
        <!-- Credit Score Dialog -->
        <v-dialog v-model="showCreditScoreDialog" max-width="800px">
            <v-card style="border-radius: 12px; overflow: hidden;">
                <v-card-title class="teal darken-2 white--text" style="background: linear-gradient(135deg, #00695C 0%, #004D40 100%);">
                    <v-icon left color="white">mdi-star-circle</v-icon>
                    <span class="headline font-weight-bold">Understanding Your Credit Score</span>
                    <v-spacer></v-spacer>
                    <v-btn icon dark @click="showCreditScoreDialog = false">
                        <v-icon>close</v-icon>
                    </v-btn>
                </v-card-title>

                <v-card-text class="pa-4" style="max-height: 600px; overflow-y: auto;">
                    <div class="credit-score-explanation">
                        <div class="mb-4">
                            <h3 class="teal--text text--darken-2 mb-2">🌟 What is a Credit Score?</h3>
                            <p class="body-2">
                                Your <strong>Credit Score</strong> serves as a tangible digital guarantee within the PettyCash ecosystem.
                                It enables Business Suite clients to access credit facilities from PettyCash SAS, specifically in the form
                                of goods and services, using their score as leverage rather than traditional cash deposits.
                            </p>
                            <div class="mt-3 pa-3" style="background: #e3f2fd; border-radius: 8px; border-left: 4px solid #2196F3;">
                                <p class="body-2 mb-0">
                                    <strong>💡 Key Concept:</strong> Your Credit Score represents your financial reliability and unlocks
                                    opportunities without requiring upfront cash deposits. The higher your score, the more financial
                                    flexibility you gain.
                                </p>
                            </div>
                        </div>

                        <div class="mb-4">
                            <h3 class="teal--text text--darken-2 mb-2">📈 Credit Accumulation Protocol</h3>
                            <p class="body-2 mb-3">
                                Your Credit Score grows through two distinct channels: <strong>Personal Status</strong> (your individual actions)
                                and <strong>Network Engagement</strong> (activities of your referred users).
                            </p>

                            <div class="mb-3">
                                <h4 class="teal--text text--darken-1 mb-2" style="font-size: 1rem;">
                                    <v-icon small color="teal" class="mr-1">mdi-account-star</v-icon>
                                    Personal Status Points
                                </h4>
                                <p class="body-2 caption grey--text mb-2">Earn points through your own activities and milestones</p>
                                <v-list dense class="elevation-1" style="border-radius: 8px;">
                                    <v-list-item>
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small color="green">mdi-check-circle</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title class="body-2">Business Account Validation: <strong>+10 points</strong></v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-list-item>
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small color="green">mdi-check-circle</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title class="body-2">On-time Campaign Completion: Bonus points</v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </div>

                            <div class="mb-3">
                                <h4 class="teal--text text--darken-1 mb-2" style="font-size: 1rem;">
                                    <v-icon small color="teal" class="mr-1">mdi-account-group</v-icon>
                                    Network Engagement Points
                                </h4>
                                <p class="body-2 caption grey--text mb-2">Earn points when your referred users take action</p>
                                <v-list dense class="elevation-1" style="border-radius: 8px;">
                                    <v-list-item>
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small color="orange">mdi-star-circle</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title class="body-2">Successful Referral: <strong>+5 points</strong></v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                    <v-list-item>
                                        <v-list-item-icon class="mr-2">
                                            <v-icon small color="orange">mdi-star-circle</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-content>
                                            <v-list-item-title class="body-2">Referred User Campaign: <strong>+5 points</strong></v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </v-list>
                            </div>
                        </div>

                        <div class="mb-4">
                            <h3 class="teal--text text--darken-2 mb-2">💎 Benefits of a Good Credit Score</h3>
                            <div class="benefits-grid">
                                <div class="benefit-item">
                                    <v-icon small color="teal" class="mr-1">mdi-truck-fast</v-icon>
                                    <span class="caption">Early Delivery at 50%</span>
                                </div>
                                <div class="benefit-item">
                                    <v-icon small color="teal" class="mr-1">mdi-shield-check</v-icon>
                                    <span class="caption">Higher Trust Level</span>
                                </div>
                                <div class="benefit-item">
                                    <v-icon small color="teal" class="mr-1">mdi-percent</v-icon>
                                    <span class="caption">Better Loan Terms</span>
                                </div>
                                <div class="benefit-item">
                                    <v-icon small color="teal" class="mr-1">mdi-star</v-icon>
                                    <span class="caption">Priority Support</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </v-card-text>

                <v-card-actions class="pa-4" style="background: #f5f5f5;">
                    <v-btn color="teal darken-2" dark @click="showCreditScoreDialog = false" style="border-radius: 24px;">
                        <v-icon left small>mdi-check</v-icon>
                        Got it!
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Hero Section with Original Primary Blue -->
        <div class="hero-section primary darken-2">
            <div class="hero-gradient"></div>
            <div class="hero-pattern"></div>

            <v-container class="hero-content">
                <div class="welcome-badge" v-if="$store.state.isAuthenticated">
                    <span class="badge-icon">😃</span>
                    <span class="badge-text">Welcome <strong class="notranslate username-text" translate="no">{{$store.state.user_info["user"]["username"]}}</strong>, to PettyCash: Dashboard Hub</span>
                </div>
                <div class="welcome-badge" v-else>
                    <span class="badge-icon">😃</span>
                    <span class="badge-text">Welcome to <strong>PettyCash: Dashboard Hub</strong></span>
                </div>

                <!-- Credit Score Badge -->
                <div class="credit-score-badge" @click="showCreditScoreDialog = true">
                    <v-icon small class="credit-icon">mdi-star-circle</v-icon>
                    <span class="credit-text">Your Credit Score: <strong>{{ userCreditScore }}</strong></span>
                    <v-icon small class="info-icon">mdi-information-outline</v-icon>
                </div>

                <h1 class="hero-title">
                    All your tools
                    <span class="hero-title-accent">in one place.</span>
                </h1>

                <!-- Animated Journey Illustration -->
                <v-layout row wrap class="journey-flow">
                    <v-flex xs2 sm3 md2 lg2 class="journey-item" style="animation-delay: 0.1s">
                        <v-btn outline fab color="white" class="journey-btn">
                            <i class="fas fa-users fa-3x"></i>
                        </v-btn>
                    </v-flex>

                    <v-flex xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.2s">
                        <v-btn flat fab color="white" class="journey-btn-flat">
                            <i class="fas fa-plus fa-2x"></i>
                        </v-btn>
                    </v-flex>

                    <v-flex xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.3s">
                        <v-btn outline fab color="white" class="journey-btn">
                            <i class="fas fa-chart-line fa-3x"></i>
                        </v-btn>
                    </v-flex>

                    <v-flex xs2 sm2 md2 lg2 class="journey-item" style="animation-delay: 0.4s">
                        <v-btn flat fab color="white" class="journey-btn-flat">
                            <i class="fas fa-arrow-right fa-2x"></i>
                        </v-btn>
                    </v-flex>

                    <v-flex xs2 sm2 md3 lg3 class="journey-item fortune-item" style="animation-delay: 0.5s">
                        <div class="fortune-glow"></div>
                        <v-img
                            :src="require('@/assets/money_bag_bill_coins_color.png')"
                            aspect-ratio="2"
                            contain
                            height="90"
                            width="120"
                            class="fortune-image"
                        ></v-img>
                    </v-flex>
                </v-layout>
            </v-container>
        </div>

        <!-- Main Content -->
        <v-container class="main-content mt-5">
            <v-layout row>
                <v-flex xs12 md10 offset-md1 lg10 offset-lg1>
                    <v-card flat class="card--flex-toolbar" color="transparent">
                        <v-container fluid grid-list-lg>
                            <!-- Section Header -->
                            <v-layout row wrap>
                                <v-flex xs12 class="mt-0 pt-0 mb-2">
                                    <h3 class="white--text section-title">Explore Dashboard Services</h3>
                                </v-flex>
                            </v-layout>

                            <!-- Cards Grid -->
                            <v-layout row wrap>
                                <v-flex
                                    xs12 sm6 md6 lg4
                                    v-for="item in visibleHubLinks"
                                    :key="item.id"
                                    class="pa-2"
                                >
                                    <v-hover v-slot="{ hover }">
                                        <v-card
                                            :class="['service-card', `elevation-${hover ? 12 : 4}`, { 'service-card--highlight': item.highlight }]"
                                            style="border-radius: 5px; font-size: small"
                                            :to="item.link"
                                        >
                                            <!-- Accent Border on Hover -->
                                            <div class="card-accent-border" :style="{ background: getAccentColor(item.color) }"></div>

                                            <!-- Gradient Overlay -->
                                            <div class="card-gradient" :style="{ background: getCardGradient(item.color) }"></div>

                                            <!-- Card Header -->
                                            <v-layout row>
                                                <v-flex xs8 sm8 md8>
                                                    <v-btn
                                                        fab
                                                        :color="item.color"
                                                        :class="['mt-2 ml-3 mb-0 mr-2 white--text icon-btn', item.color]"
                                                    >
                                                        <i :class="item.icon" class="icon-display"></i>
                                                    </v-btn>
                                                </v-flex>

                                                <!-- Menu -->
                                                <v-flex xs4 sm4 md4 v-if="!(item.title == 'Legal Bindings')" class="text-xs-right">
                                                    <v-menu bottom right>
                                                        <template v-slot:activator="{ on }">
                                                            <v-btn
                                                                icon
                                                                flat
                                                                fab
                                                                class="menu-btn mt-2 ml-3 mb-0"
                                                                v-on="on"
                                                                @click.prevent="activateMenu(item.title)"
                                                            >
                                                                <i class="fa-solid fa-ellipsis-vertical fa-2x"></i>
                                                            </v-btn>
                                                        </template>
                                                        <v-list class="grey lighten-4 menu-list">
                                                            <v-list-tile
                                                                v-for="menuLink in menuItems"
                                                                :key="menuLink.title"
                                                                :to="menuLink.link"
                                                                class="menu-item"
                                                            >
                                                                <v-list-tile-title>{{ menuLink.title }}</v-list-tile-title>
                                                            </v-list-tile>
                                                        </v-list>
                                                    </v-menu>
                                                </v-flex>
                                            </v-layout>

                                            <!-- Card Content -->
                                            <v-card-title primary-title class="pb-0 mt-0 pt-1" style="color: #424242">
                                                <div class="card-content">
                                                    <h5 class="subheading pl-0 pb-0 mb-0 card-title"><b>{{ item.title }}</b></h5>
                                                    <div class="caption mt-0 mb-0 pb-2 card-description">{{ item.description }}</div>

                                                    <div class="card-footer">
                                                        <span class="card-link primary--text text--darken-2">
                                                            Open
                                                            <i class="fas fa-arrow-right card-arrow-icon"></i>
                                                        </span>
                                                    </div>
                                                </div>
                                            </v-card-title>

                                            <!-- Highlight Badge -->
                                            <div v-if="item.highlight" class="highlight-badge">
                                                <i class="fas fa-star"></i>
                                                New
                                            </div>
                                        </v-card>
                                    </v-hover>
                                </v-flex>
                            </v-layout>
                        </v-container>
                    </v-card>
                </v-flex>
            </v-layout>
        </v-container>
    </div>
</template>

<script>
import axios from "axios";

export const hubLinks = [
    {
        id: "2",
        title: "Client of Affiliates",
        description: "Manage and track clients associated with your affiliate network.",
        color: "blue",
        icon: "fa-solid fa-users fa-2x",
        link: "/business-account/dashboard/client-of-affiliates",
        sellerOnly: false,
    },
    {
        id: "5",
        title: "Client Commissions",
        description: "View and track your affiliate commissions in real time.",
        color: "orange",
        icon: "fa-solid fa-coins fa-2x",
        link: "/business-account/dashboard/commissions/client",
        sellerOnly: false,
    },
    {
        id: "7",
        title: "Create Njangi Group",
        description: "Start your own njangi group and invite members.",
        color: "teal",
        icon: "fa-solid fa-users fa-2x",
        link: "/business-account/dashboard/create-njangi-group",
        sellerOnly: false,
    },
    {
        id: "8",
        title: "My Groups (Admin)",
        description: "Manage the groups where you are the administrator.",
        icon: "fa-solid fa-shield-alt fa-2x",
        color: "indigo",
        link: "/business-account/dashboard/njangi/admin/groups",
        sellerOnly: false,
    },
    {
        id: "3",
        title: "Affiliate Marketing Tutorial",
        description: "Learn how to use our platform with step-by-step video guides.",
        color: "green",
        icon: "fa-solid fa-video fa-2x",
        link: "/business-account/dashboard/video-tutorial",
        sellerOnly: false,
    },
    {
        id: "4",
        title: "Extra Advantages",
        description: "Discover additional benefits and features available to your account.",
        color: "purple",
        icon: "fa-solid fa-gift fa-2x",
        link: "/business-account/dashboard/extra-advantages",
        sellerOnly: false,
    },
    {
        id: "9",
        title: "Become a Vendor",
        description: "Apply to list and sell your own products on the PettyCash marketplace.",
        color: "deep-orange",
        icon: "fa-solid fa-store fa-2x",
        link: "/business-account/market_place/seller/apply",
        sellerOnly: false,
        hideWhenSeller: true,
    },
    {
        id: "10",
        title: "Vendor Dashboard",
        description: "View your Vendor balance, products, and transfer earnings to your wallet or savings.",
        color: "cyan darken-2",
        icon: "fa-solid fa-chart-line fa-2x",
        link: "/business-account/market_place/seller/dashboard",
        sellerOnly: true,
    },
    {
        id: "11",
        title: "My Vendor Products",
        description: "Manage the products you have listed on the marketplace.",
        color: "teal darken-1",
        icon: "fa-solid fa-box-open fa-2x",
        link: "/business-account/market_place/seller/products",
        sellerOnly: true,
    },
    {
        id: "12",
        title: "Become a Reseller",
        description: "Apply to resell verified seller products with your own markup and earn on every sale.",
        color: "indigo",
        icon: "fa-solid fa-cart-arrow-down fa-2x",
        link: "/business-account/market_place/reseller/apply",
        sellerOnly: false,
        hideWhenReseller: true,
    },
    {
        id: "13",
        title: "Reseller Dashboard",
        description: "Track your reseller earnings, manage your catalog, and transfer resale funds.",
        color: "indigo darken-3",
        icon: "fa-solid fa-chart-pie fa-2x",
        link: "/business-account/market_place/reseller/dashboard",
        resellerOnly: true,
    },
    {
        id: "14",
        title: "My Reseller Products",
        description: "Browse the marketplace catalog and manage the products you resell.",
        color: "blue darken-3",
        icon: "fa-solid fa-boxes-packing fa-2x",
        link: "/business-account/market_place/reseller/products",
        resellerOnly: true,
    },
];

export default {
    data() {
        return {
            userCreditScore: 0,
            showCreditScoreDialog: false,
            isSeller: false,
            isReseller: false,
            menuItems: [],
        }
    },
    computed: {
        visibleHubLinks() {
            return hubLinks.filter(item => {
                if (item.hideWhenSeller && this.isSeller) return false;
                if (item.hideWhenReseller && this.isReseller) return false;
                if (item.sellerOnly && !this.isSeller) return false;
                if (item.resellerOnly && !this.isReseller) return false;
                return true;
            });
        },
    },
    mounted() {
        document.title = "PettyCash | Dashboard Hub"
        this.getUserCreditScore();
        this.checkSellerStatus();
        this.checkResellerStatus();
    },
    methods: {
        async getUserCreditScore() {
            try {
                if (this.$store.state.user_info && this.$store.state.user_info.user_credit && this.$store.state.user_info.user_credit[0]) {
                    this.userCreditScore = this.$store.state.user_info.user_credit[0].credit || 0;
                } else {
                    const response = await axios.get('/api/v1/home/get/user/info/');
                    if (response.data[0] && response.data[0].user_credit && response.data[0].user_credit[0]) {
                        this.userCreditScore = response.data[0].user_credit[0].credit || 0;
                    }
                }
            } catch (error) {
                console.error('Error fetching credit score:', error);
                this.userCreditScore = 0;
            }
        },
        async checkSellerStatus() {
            try {
                const response = await axios.get('/api/v1/market_place/seller/check-status/');
                this.isSeller = response.data.is_seller === true;
            } catch (error) {
                console.error('Error checking seller status:', error);
                this.isSeller = false;
            }
        },
        async checkResellerStatus() {
            try {
                const response = await axios.get('/api/v1/market_place/reseller/check-status/');
                this.isReseller = response.data.is_reseller === true;
            } catch (error) {
                console.error('Error checking reseller status:', error);
                this.isReseller = false;
            }
        },
        activateMenu(title) {
            if (title == "Client of Affiliates") {
                this.menuItems = [
                    { "title": "All Clients", "link": "/business-account/dashboard/client-of-affiliates" },
                    { "title": "Add New Client", "link": "/business-account/dashboard/AddUser" },
                ]
            }
            else if (title == "Client Commissions") {
                this.menuItems = [
                    { "title": "View Commissions", "link": "/business-account/dashboard/commissions/client" },
                    { "title": "Commission History", "link": "/business-account/dashboard/commissions/history" },
                ]
            }
            else if (title == "Create Njangi Group") {
                this.menuItems = [
                    { "title": "Create New", "link": "/business-account/dashboard/create-njangi-group" },
                    { "title": "My Groups", "link": "/business-account/dashboard/njangi/admin/groups" },
                ]
            }
            else if (title == "My Groups (Admin)") {
                this.menuItems = [
                    { "title": "Active Groups", "link": "/business-account/dashboard/njangi/admin/groups" },
                    { "title": "Create Group", "link": "/business-account/dashboard/create-njangi-group" },
                ]
            }
            else if (title == "Affiliate Marketing Tutorial") {
                this.menuItems = [
                    { "title": "Getting Started", "link": "/business-account/dashboard/video-tutorial/getting-started" },
                    { "title": "Advanced Features", "link": "/business-account/dashboard/video-tutorial/advanced-features" },
                ]
            }
            else if (title == "Extra Advantages") {
                this.menuItems = [
                    { "title": "All Benefits", "link": "/business-account/dashboard/extra-advantages" },
                    { "title": "Upgrade Plan", "link": "/business-account/dashboard/upgrade" },
                ]
            }
            else if (title == "Become a Vendor") {
                this.menuItems = [
                    { "title": "Apply Now", "link": "/business-account/market_place/seller/apply" },
                    { "title": "Requirements", "link": "/business-account/market_place/seller/requirements" },
                ]
            }
            else if (title == "Vendor Dashboard") {
                this.menuItems = [
                    { "title": "Overview", "link": "/business-account/market_place/seller/dashboard" },
                    { "title": "Earnings", "link": "/business-account/market_place/seller/earnings" },
                    { "title": "Transfers", "link": "/business-account/market_place/seller/transfers" },
                ]
            }
            else if (title == "My Vendor Products") {
                this.menuItems = [
                    { "title": "All Products", "link": "/business-account/market_place/seller/products" },
                    { "title": "Add Product", "link": "/business-account/market_place/seller/products/add" },
                ]
            }
            else if (title == "Become a Reseller") {
                this.menuItems = [
                    { "title": "Apply Now", "link": "/business-account/market_place/reseller/apply" },
                ]
            }
            else if (title == "Reseller Dashboard") {
                this.menuItems = [
                    { "title": "Overview", "link": "/business-account/market_place/reseller/dashboard" },
                    { "title": "Catalog", "link": "/business-account/market_place/reseller/products" },
                ]
            }
            else if (title == "My Reseller Products") {
                this.menuItems = [
                    { "title": "My Catalog", "link": "/business-account/market_place/reseller/products" },
                ]
            }
        },
        getCardGradient(color) {
            const gradients = {
                orange: 'linear-gradient(135deg, rgba(255, 152, 0, 0.04) 0%, rgba(255, 152, 0, 0) 100%)',
                purple: 'linear-gradient(135deg, rgba(156, 39, 176, 0.04) 0%, rgba(156, 39, 176, 0) 100%)',
                teal: 'linear-gradient(135deg, rgba(0, 150, 136, 0.04) 0%, rgba(0, 150, 136, 0) 100%)',
                blue: 'linear-gradient(135deg, rgba(33, 150, 243, 0.04) 0%, rgba(33, 150, 243, 0) 100%)',
                green: 'linear-gradient(135deg, rgba(76, 175, 80, 0.04) 0%, rgba(76, 175, 80, 0) 100%)',
                indigo: 'linear-gradient(135deg, rgba(63, 81, 181, 0.04) 0%, rgba(63, 81, 181, 0) 100%)',
                'deep-orange': 'linear-gradient(135deg, rgba(255, 87, 34, 0.04) 0%, rgba(255, 87, 34, 0) 100%)',
                'cyan darken-2': 'linear-gradient(135deg, rgba(0, 172, 193, 0.04) 0%, rgba(0, 172, 193, 0) 100%)',
                'teal darken-1': 'linear-gradient(135deg, rgba(0, 137, 123, 0.04) 0%, rgba(0, 137, 123, 0) 100%)',
                'indigo darken-3': 'linear-gradient(135deg, rgba(26, 35, 126, 0.04) 0%, rgba(26, 35, 126, 0) 100%)',
                'blue darken-3': 'linear-gradient(135deg, rgba(13, 71, 161, 0.04) 0%, rgba(13, 71, 161, 0) 100%)',
            };
            return gradients[color] || gradients.blue;
        },
        getAccentColor(color) {
            const accents = {
                orange: '#FF9800',
                purple: '#9C27B0',
                teal: '#009688',
                blue: '#2196F3',
                green: '#4CAF50',
                indigo: '#3F51B5',
                'deep-orange': '#FF5722',
                'cyan darken-2': '#00ACC1',
                'teal darken-1': '#00897B',
                'indigo darken-3': '#1A237E',
                'blue darken-3': '#0D47A1',
            };
            return accents[color] || accents.blue;
        }
    },
}
</script>

<style scoped>
/* Google Fonts Import */
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=DM+Sans:wght@400;500;700&display=swap');

.hub-page {
    min-height: 100vh;
    background: #f5f5f5;
    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ===== HERO SECTION ===== */
.hero-section {
    position: relative;
    padding: 40px 0;
    min-height: 420px;
    overflow: hidden;
}

.hero-gradient {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background:
        radial-gradient(circle at 20% 50%, rgba(156, 39, 176, 0.12) 0%, transparent 50%),
        radial-gradient(circle at 80% 30%, rgba(255, 152, 0, 0.08) 0%, transparent 50%);
    pointer-events: none;
}

.hero-pattern {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image:
        radial-gradient(circle at 20px 20px, rgba(255, 255, 255, 0.02) 2px, transparent 0);
    background-size: 40px 40px;
    pointer-events: none;
}

.hero-content {
    position: relative;
    z-index: 1;
    text-align: center;
}

.welcome-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.12);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    padding: 8px 16px;
    border-radius: 50px;
    color: white;
    font-size: 14px;
    margin-bottom: 12px;
    animation: slideInDown 0.6s ease-out;
}

.badge-icon {
    font-size: 16px;
}

.badge-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.badge-text strong {
    font-weight: 700;
}

/* Credit Score Badge */
.credit-score-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 165, 0, 0.2));
    backdrop-filter: blur(10px);
    border: 2px solid rgba(255, 215, 0, 0.5);
    padding: 10px 20px;
    border-radius: 50px;
    color: white;
    font-size: 15px;
    margin-bottom: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    animation: pulseGlow 2s infinite, slideInDown 0.6s ease-out 0.1s both;
}

.credit-score-badge:hover {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 165, 0, 0.3));
    transform: scale(1.05);
    box-shadow: 0 0 25px rgba(255, 215, 0, 0.6);
}

.credit-icon {
    color: #FFD700;
    font-size: 20px !important;
}

.credit-text {
    font-weight: 500;
}

.credit-text strong {
    font-weight: 700;
    color: #FFD700;
}

.info-icon {
    color: #FFD700;
    font-size: 18px !important;
}

@keyframes pulseGlow {
    0%, 100% {
        box-shadow: 0 0 10px rgba(255, 215, 0, 0.4);
    }
    50% {
        box-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 165, 0, 0.5);
    }
}

.hero-title {
    font-family: 'Poppins', sans-serif;
    font-size: clamp(20px, 4vw, 28px);
    font-weight: 600;
    color: white;
    line-height: 1.3;
    margin-bottom: 32px;
    animation: slideInDown 0.6s ease-out 0.2s both;
    padding: 0 16px;
}

.hero-title-accent {
    display: block;
    background: linear-gradient(135deg, #FFD54F 0%, #FF9800 50%, #FF6F00 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-top: 4px;
    font-weight: 700;
}

/* ===== JOURNEY FLOW ===== */
.journey-flow {
    animation: fadeInUp 0.6s ease-out 0.3s both;
    justify-content: center;
    align-items: center;
}

.journey-item {
    display: flex;
    justify-content: center;
    align-items: center;
}

.journey-btn {
    border: 2px solid white !important;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.journey-btn:hover {
    transform: translateY(-5px) scale(1.05) !important;
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25) !important;
}

.journey-btn-flat {
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
}
.journey-btn-flat:hover {
    transform: scale(1.1) !important;
}

.fortune-item {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
}

.fortune-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 140px;
    height: 140px;
    background: radial-gradient(circle, rgba(255, 193, 7, 0.25) 0%, transparent 70%);
    border-radius: 50%;
    animation: pulse 2s ease-in-out infinite;
    pointer-events: none;
}

@keyframes pulse {
    0%, 100% {
        transform: translate(-50%, -50%) scale(1);
        opacity: 0.5;
    }
    50% {
        transform: translate(-50%, -50%) scale(1.1);
        opacity: 0.7;
    }
}

.fortune-image {
    position: relative;
    z-index: 1;
    filter: drop-shadow(0 4px 12px rgba(255, 193, 7, 0.3));
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fortune-item:hover .fortune-image {
    transform: scale(1.05) rotate(5deg);
}

/* ===== MAIN CONTENT ===== */
.card--flex-toolbar {
    margin-top: -124px;
}

.section-title {
    font-size: 18px;
    font-weight: 400;
    animation: fadeInUp 0.6s ease-out 0.3s both;
}

/* ===== SERVICE CARDS ===== */
.service-card {
    position: relative;
    background: white;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: hidden;
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Accent border that appears on hover */
.card-accent-border {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    opacity: 0;
    transition: opacity 0.4s;
}

.service-card:hover .card-accent-border {
    opacity: 1;
}

.service-card--highlight {
    border: 2px solid #9C27B0 !important;
    box-shadow: 0 4px 16px rgba(156, 39, 176, 0.2) !important;
}

.card-gradient {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
}

.service-card:hover .card-gradient {
    opacity: 1;
}

.icon-btn {
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: visible;
}

.icon-btn .icon-display {
    position: relative;
    z-index: 2;
    color: white !important;
}

/* Force explicite de la couleur blanche pour Font Awesome */
.icon-btn i {
    color: #ffffff !important;
    position: relative;
    z-index: 2;
}

/* Si nécessaire, ajoutez aussi pour les pseudo-éléments */
.icon-btn i::before {
    color: #ffffff !important;
}

.service-card:hover .icon-btn {
    transform: scale(1.08) rotate(5deg) !important;
}

.icon-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
    z-index: 5;
}

.service-card:hover .icon-glow {
    opacity: 1;
}

.menu-btn {
    transition: all 0.3s !important;
}

/* ===== CARD CONTENT ===== */
.card-content {
    width: 100%;
    flex: 1;
    display: flex;
    flex-direction: column;
}

.card-title {
    font-size: 16px;
    line-height: 1.3;
}

.card-description {
    font-size: 12px;
    line-height: 1.5;
    flex: 1;
}

.card-footer {
    display: flex;
    align-items: center;
    padding-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin-top: 8px;
}

.card-link {
    font-size: 13px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: gap 0.3s;
}

.service-card:hover .card-link {
    gap: 10px;
}

.card-arrow-icon {
    font-size: 11px !important;
    display: inline-block;
    transition: transform 0.3s;
}

.service-card:hover .card-arrow-icon {
    transform: translateX(3px);
}

/* ===== HIGHLIGHT BADGE ===== */
.highlight-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background: linear-gradient(135deg, #9C27B0, #7B1FA2);
    color: white;
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 4px;
    box-shadow: 0 2px 8px rgba(156, 39, 176, 0.3);
    animation: bounce 2s ease-in-out infinite;
    z-index: 2;
}

.highlight-badge i {
    font-size: 9px;
}

@keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-3px); }
}

/* ===== MENU ===== */
.menu-list {
    border-radius: 4px !important;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.menu-item {
    transition: background 0.2s;
    font-size: 14px;
}

.menu-item:hover {
    background: rgba(0, 0, 0, 0.05) !important;
}

/* ===== ANIMATIONS ===== */
@keyframes fadeInUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes slideInDown {
    from {
        opacity: 0;
        transform: translateY(-15px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* ===== RESPONSIVE DESIGN ===== */

/* Extra Small Devices (phones, less than 600px) */
@media (max-width: 599px) {
    .hero-section {
        min-height: 300px;
        padding: 24px 0;
    }

    .welcome-badge {
        font-size: 12px;
        padding: 6px 12px;
        max-width: 90%;
    }

    .hero-title {
        font-size: 16px;
        margin-bottom: 24px;
    }

    .journey-flow {
        margin-top: 16px;
    }

    .journey-btn,
    .journey-btn-flat {
        width: 48px !important;
        height: 48px !important;
        min-width: 48px !important;
    }

    .journey-btn i.fa-3x {
        font-size: 1.5rem !important;
    }

    .journey-btn-flat i.fa-2x {
        font-size: 1rem !important;
    }

    .fortune-image {
        height: 60px !important;
        width: 80px !important;
    }

    .fortune-glow {
        width: 100px;
        height: 100px;
    }

    .card--flex-toolbar {
        margin-top: -80px;
    }

    .section-title {
        font-size: 16px;
    }

    .service-card {
        margin-bottom: 8px;
    }

    .icon-btn {
        width: 48px !important;
        height: 48px !important;
    }

    .icon-btn i {
        font-size: 1.2rem !important;
    }

    .card-title {
        font-size: 14px !important;
    }

    .card-description {
        font-size: 11px !important;
    }

    .card-link {
        font-size: 12px;
    }

    .highlight-badge {
        font-size: 8px;
        padding: 3px 8px;
    }
}

/* Small Devices (tablets, 600px to 959px) */
@media (min-width: 600px) and (max-width: 959px) {
    .hero-section {
        min-height: 340px;
        padding: 32px 0;
    }

    .welcome-badge {
        font-size: 13px;
    }

    .hero-title {
        font-size: 20px;
    }

    .journey-btn,
    .journey-btn-flat {
        width: 56px !important;
        height: 56px !important;
    }

    .journey-btn i.fa-3x {
        font-size: 1.8rem !important;
    }

    .journey-btn-flat i.fa-2x {
        font-size: 1.2rem !important;
    }

    .fortune-image {
        height: 75px !important;
        width: 100px !important;
    }

    .card--flex-toolbar {
        margin-top: -100px;
    }

    .section-title {
        font-size: 17px;
    }
}

/* Medium Devices (small laptops, 960px to 1263px) */
@media (min-width: 960px) and (max-width: 1263px) {
    .hero-section {
        min-height: 360px;
    }

    .card--flex-toolbar {
        margin-top: -124px;
    }
}

/* Large Devices (desktops, 1264px and up) */
@media (min-width: 1264px) {
    .hero-section {
        min-height: 380px;
    }

    .hero-title {
        font-size: 28px;
    }
}

/* Landscape orientation on mobile */
@media (max-width: 959px) and (orientation: landscape) {
    .hero-section {
        min-height: 260px;
        padding: 20px 0;
    }

    .hero-title {
        font-size: 18px;
        margin-bottom: 16px;
    }

    .journey-flow {
        margin-top: 12px;
    }

    .card--flex-toolbar {
        margin-top: -124px;
    }
}

/* Print styles */
@media print {
    .hero-gradient,
    .hero-pattern,
    .fortune-glow,
    .icon-glow,
    .card-gradient {
        display: none;
    }

    .service-card {
        page-break-inside: avoid;
    }
}

icon-btn {
    position: relative;
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
    overflow: visible !important;
}

.icon-btn .icon-display {
    position: relative;
    color: white !important;
}

/* Force explicite de la couleur blanche pour Font Awesome */
.icon-btn i {
    color: #ffffff !important;
    position: relative;
    z-index: 10;
}

/* Si nécessaire, ajoutez aussi pour les pseudo-éléments */
.icon-btn i::before {
    color: #ffffff !important;
}

.service-card:hover .icon-btn {
    transform: scale(1.08) rotate(5deg) !important;
}

/* CORRECTION: Utiliser filter au lieu de background pour l'effet glow */
.icon-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    border-radius: 50%;
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
    z-index: 0;
    background: transparent !important;
}

.service-card:hover .icon-glow {
    opacity: 1;
}

/* Appliquer l'effet glow avec box-shadow sur le bouton lui-même */
.service-card:hover .icon-btn::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 120%;
    height: 120%;
    border-radius: 50%;
    opacity: 0.3;
    z-index: 0;
    pointer-events: none;
}

/* Effets glow par couleur */
.service-card:hover .icon-btn.orange::before {
    box-shadow: 0 0 30px 10px rgba(255, 152, 0, 0.5);
}

.service-card:hover .icon-btn.purple::before {
    box-shadow: 0 0 30px 10px rgba(156, 39, 176, 0.5);
}

.service-card:hover .icon-btn.teal::before {
    box-shadow: 0 0 30px 10px rgba(0, 150, 136, 0.5);
}

.service-card:hover .icon-btn.gold::before {
    box-shadow: 0 0 30px 10px rgba(255, 193, 7, 0.5);
}

.service-card:hover .icon-btn.blue::before {
    box-shadow: 0 0 30px 10px rgba(33, 150, 243, 0.5);
}

.service-card:hover .icon-btn.green::before {
    box-shadow: 0 0 30px 10px rgba(76, 175, 80, 0.5);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 599px) {
    .hero-section {
        min-height: 340px;
        padding: 24px 0;
    }

    .welcome-badge,
    .credit-score-badge {
        font-size: 12px;
        padding: 6px 12px;
        max-width: 90%;
    }

    .hero-title {
        font-size: 16px;
        margin-bottom: 24px;
    }

    .journey-btn,
    .journey-btn-flat {
        width: 48px !important;
        height: 48px !important;
        min-width: 48px !important;
    }

    .journey-btn i.fa-3x {
        font-size: 1.5rem !important;
    }

    .journey-btn-flat i.fa-2x {
        font-size: 1rem !important;
    }

    .fortune-image {
        height: 60px !important;
        width: 80px !important;
    }

    .card--flex-toolbar {
        margin-top: -100px;
    }
}

.hero-section {
min-height: 380px;
}
.card--flex-toolbar {
    margin-top: -119px;
}

/* ===== ICON SIZE FIX: fa-users & fa-chart-line in journey buttons ===== */
.journey-btn {
    overflow: hidden !important;
}

.journey-btn i.fa-users,
.journey-btn i.fa-chart-line {
    font-size: 1.4rem !important;
    line-height: 1 !important;
}

/* Small devices (tablets, 600px to 959px) */
@media (min-width: 600px) and (max-width: 959px) {
    .journey-btn i.fa-users,
    .journey-btn i.fa-chart-line {
        font-size: 1.2rem !important;
    }
}

/* Extra small devices (phones, less than 600px) */
@media (max-width: 599px) {
    .journey-btn i.fa-users,
    .journey-btn i.fa-chart-line {
        font-size: 1.0rem !important;
    }
}

/* Landscape mobile */
@media (max-width: 959px) and (orientation: landscape) {
    .journey-btn i.fa-users,
    .journey-btn i.fa-chart-line {
        font-size: 0.9rem !important;
    }
}
</style>